import {
    Action,
    combineReducers,
    configureStore,
    createSelector,
    ThunkDispatch,
} from "@reduxjs/toolkit";
import {
    CheckType,
    Country,
    Currency,
    Enums,
    Groups,
    Timezone,
} from "Interfaces/Utils/Enums";
import { Integration } from "Interfaces/Utils/Integrations";
import { useDispatch } from "react-redux";
import {
    createMigrate,
    FLUSH,
    PAUSE,
    PERSIST,
    persistReducer,
    PURGE,
    REGISTER,
    REHYDRATE,
} from "redux-persist";
import { PersistPartial } from "redux-persist/es/persistReducer";
import storage from "redux-persist/lib/storage";
import {
    AuthSliceStateType,
    ClientTreeSliceStateType,
    DatatableSliceStateType,
    FiltersSliceStateType,
    MerchantSliceStateType,
    NotificationSliceStateType,
    SnackbarSliceStateType,
    switchDashboardSliceStateType,
    UISliceStateType,
} from "./Interfaces/Redux/sliceStateTypes";
import authSlice from "./Slices/authSlice";
import { campaignsTablePreferenceSliceReducer } from "./Slices/campaignsTablePreferenceSlice";
import clientTreeSlice from "./Slices/clientTreeSlice";
import customersTablePreferenceSlice from "./Slices/customersTablePreferenceSlice";
import { failedNotificationsTablePreferenceSliceReducer } from "./Slices/failedNotificationsTablePreferenceSlice";
import filtersSlice from "./Slices/filtersSlice";
import merchantSlice from "./Slices/merchantSlice";
import migrations from "./Slices/migrations";
import notificationsSlice from "./Slices/notificationsSlice";
import { ordersMotoTablePreferenceSliceReducer } from "./Slices/ordersMotoTablePreferenceSlice";
import ordersTablePreferenceSlice from "./Slices/ordersTablePreferenceSlice";
import publicFolderDataSlice from "./Slices/publicFolderDataSlice";
import snackbarSlice from "./Slices/snackbarSlice";
import switchDashboardSlice from "./Slices/switchDashboardSlice";
import UISlice from "./Slices/UISlice";

const reducers = combineReducers({
    ui: UISlice,
    auth: authSlice,
    clientTree: clientTreeSlice,
    snackbar: snackbarSlice,
    filters: filtersSlice,
    userNotifications: notificationsSlice,
    ordersTablePreference: ordersTablePreferenceSlice,
    customersTablePreference: customersTablePreferenceSlice,
    campaignsTablePreference: campaignsTablePreferenceSliceReducer,
    ordersMotoTablePreference: ordersMotoTablePreferenceSliceReducer,
    failedNotificationsTablePreference:
        failedNotificationsTablePreferenceSliceReducer,
    switchDashboard: switchDashboardSlice,
    merchant: merchantSlice,
    publicFolderData: publicFolderDataSlice,
});

const persistConfig = {
    key: "root",
    version: 15,
    storage,
    whitelist: [
        "ui",
        "ordersTablePreference",
        "customersTablePreference",
        "campaignsTablePreference",
        "ordersMotoTablePreference",
        "failedNotificationsTablePreference",
        "switchDashboard",
    ],
    blacklist: ["auth", "snackbar", "filters", "clientTree", "merchant"],
    migrate: createMigrate(migrations, {
        debug: process.env.NODE_ENV !== "production",
    }),
};
const persistedReducer = persistReducer(persistConfig, reducers);

const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                ],
            },
        }),
    devTools: process.env.NODE_ENV !== "production",
});

export type RootState = ReturnType<typeof reducers> & {
    _persist: PersistPartial;
};

export type ThunkAppDispatch = ThunkDispatch<RootState, void, Action>;

export const useAppThunkDispatch = (): ThunkAppDispatch =>
    useDispatch<ThunkAppDispatch>();
export const selectAuth = (state: RootState): AuthSliceStateType => state.auth;
export const selectSwitchDashboard = (
    state: RootState,
): switchDashboardSliceStateType => state.switchDashboard;
export const selectUI = (state: RootState): UISliceStateType => state.ui;
export const selectSnackbar = (state: RootState): SnackbarSliceStateType =>
    state.snackbar;
export const selectFilters = (state: RootState): FiltersSliceStateType =>
    state.filters;
export const selectUserNotifications = (
    state: RootState,
): NotificationSliceStateType => state.userNotifications;
export const selectClientTree = (state: RootState): ClientTreeSliceStateType =>
    state.clientTree;

export const selectOrdersTablePreference = (
    state: RootState,
): DatatableSliceStateType => state.ordersTablePreference;
export const selectCustomersTablePreference = (
    state: RootState,
): DatatableSliceStateType => state.customersTablePreference;
export const selectCampaignsTablePreference = (
    state: RootState,
): DatatableSliceStateType => state.campaignsTablePreference;
export const selectOrdersMotoTablePreference = (
    state: RootState,
): DatatableSliceStateType => state.ordersMotoTablePreference;
export const selectFailedNotificationsTablePreference = (
    state: RootState,
): DatatableSliceStateType => state.failedNotificationsTablePreference;
export const selectMerchant = (state: RootState): MerchantSliceStateType =>
    state.merchant;
//Public folder data entities
export const selectEnums = (state: RootState): Enums | undefined => {
    const data = state.publicFolderData.enums;
    if (!data) {
        return undefined;
    }
    return data;
};
const EMPTY_ARRAY: unknown[] = [];

export const selectIntegrations = (state: RootState): Integration[] => {
    const data = state.publicFolderData.integrations;
    return data || (EMPTY_ARRAY as Integration[]);
};
export const selectCountries = createSelector(
    selectEnums,
    (enums) => enums?.countries || (EMPTY_ARRAY as Country[]),
);
export const selectGroups = createSelector(
    selectEnums,
    (enums) => enums?.groups || (EMPTY_ARRAY as Groups[]),
);
export const selectNonAdminGroups = createSelector(
    selectEnums,
    (enums) => enums?.non_admin_groups || (EMPTY_ARRAY as Groups[]),
);
export const selectAcquirerTypes = createSelector(
    selectEnums,
    (enums) => enums?.acquirer_types || (EMPTY_ARRAY as string[]),
);
export const selectCheckoutSettingTypes = createSelector(
    selectEnums,
    (enums) => enums?.checkout_setting_types || (EMPTY_ARRAY as string[]),
);
export const selectCheckoutThemeTypes = createSelector(
    selectEnums,
    (enums) => enums?.checkout_theme_types || (EMPTY_ARRAY as string[]),
);
export const selectCheckTypes = createSelector(
    selectEnums,
    (enums) => enums?.checktypes || (EMPTY_ARRAY as CheckType[]),
);
export const selectCategories = createSelector(
    selectEnums,
    (enums) => enums?.categories || (EMPTY_ARRAY as string[]),
);
export const selectCardBrands = createSelector(
    selectEnums,
    (enums) => enums?.card_brands || (EMPTY_ARRAY as string[]),
);
export const selectCofReasons = createSelector(
    selectEnums,
    (enums) => enums?.cof_reasons || (EMPTY_ARRAY as string[]),
);
export const selectCurrencies = createSelector(
    selectEnums,
    (enums) => enums?.currencies || (EMPTY_ARRAY as Currency[]),
);
export const selectDccModes = createSelector(
    selectEnums,
    (enums) => enums?.dcc_modes || (EMPTY_ARRAY as string[]),
);
export const selectListTypes = createSelector(
    selectEnums,
    (enums) => enums?.list_types || (EMPTY_ARRAY as string[]),
);
export const selectOperatives = createSelector(
    selectEnums,
    (enums) => enums?.operatives || (EMPTY_ARRAY as string[]),
);
export const selectPaymentTypes = createSelector(
    selectEnums,
    (enums) => enums?.payment_types || (EMPTY_ARRAY as string[]),
);
export const selectServiceTypes = createSelector(
    selectEnums,
    (enums) => enums?.serviceTypes || (EMPTY_ARRAY as string[]),
);
export const selectTimezones = createSelector(
    selectEnums,
    (enums) => enums?.timezones || (EMPTY_ARRAY as Timezone[]),
);
export const selectSourceTypes = createSelector(
    selectEnums,
    (enums) => enums?.source_types || (EMPTY_ARRAY as string[]),
);
export const selectTemplateTypes = createSelector(
    selectEnums,
    (enums) => enums?.template_types || (EMPTY_ARRAY as string[]),
);
export const selectOrderTags = createSelector(
    selectEnums,
    (enums) => enums?.order_tags || (EMPTY_ARRAY as string[]),
);
export const selectOrderTypes = createSelector(
    selectEnums,
    (enums) => enums?.order_types || (EMPTY_ARRAY as string[]),
);
export const selectOrderStatuses = createSelector(
    selectEnums,
    (enums) => enums?.order_statuses || (EMPTY_ARRAY as string[]),
);
export const selectTransactionStatuses = createSelector(
    selectEnums,
    (enums) => enums?.transaction_statuses || (EMPTY_ARRAY as string[]),
);
export const selectLanguages = createSelector(
    selectEnums,
    (enums) => enums?.languages || (EMPTY_ARRAY as string[]),
);
export const selectMotoPaymentStatuses = createSelector(
    selectEnums,
    (enums) => enums?.moto_payment_statuses || (EMPTY_ARRAY as string[]),
);
export default store;
